<!-- * @author Amisha Anshu -->
<!-- * @description chat component  -->

<!-- Added by Amisha on 2026-09-09.
Reason: make all non-interactive chat areas draggable and apply the active-chat attention animation without showing text. -->
<div
  #chatWindow
  class="chat-window"
  [class.chat-attention-highlight]="attentionHighlighted"
  (animationend)="onAttentionAnimationEnd($event)"
  (mousedown)="onDragStart($event)"
>
  <!-- Top drag bar -->
  <div class="chat-dragbar">
    <button *ngIf="showDragbarClose" type="button" class="close-btn chat-dragbar-close" (click)="closeChat()"
      aria-label="Close chat">
      <span class="material-icons">close</span>
    </button>
  </div>

  <!-- Header -->
  <div class="chat-header">
    <div class="chat-title" *ngIf="!isLoadingMessages || (displayApplicationNumber && displayApplicantName); else headerLoader">
      <wm-common-text class="chat-title-application" [data]="{ text: 'Application # ' + displayApplicationNumber }"></wm-common-text>
      <span class="divider">|</span>
      <wm-common-text class="chat-title-name" [attr.title]="displayApplicantName" [data]="{ text: displayApplicantName }"></wm-common-text>
    </div>

    <ng-template #headerLoader>
      <div class="chat-title chat-title-loading" aria-live="polite">
        <wm-common-text class="chat-title-application" [data]="{ text: 'Application #' }"></wm-common-text>
        <span class="chat-header-inline-skeleton chat-header-inline-skeleton-application" aria-hidden="true"></span>
        <span class="divider">|</span>
        <span class="chat-header-inline-skeleton chat-header-inline-skeleton-name" aria-hidden="true"></span>
      </div>
    </ng-template>

    <div class="chat-header-actions">
      <!-- Added by Amisha on 2026-07-02.
           Reason: staff need a mail action beside Print Transcript, with hover text still available when the button is disabled. -->
      <span *ngIf="isStaff" class="chat-header-action-wrapper" [attr.title]="sendMailTooltip">
        <button type="button" class="chat-header-print-btn" [disabled]="isSendMailDisabled" (click)="onSendMail()"
          aria-label="Send mail to applicant">
          <span class="material-icons-outlined chat-header-mail-icon">mail_outline</span>
          <wm-common-text class="chat-header-print-text" [data]="{ text: 'Send Mail' }"></wm-common-text>
        </button>
      </span>

      <button *ngIf="isStaff" type="button" class="chat-header-print-btn" [disabled]="isPrintDisabled"
        (click)="onPrint()" aria-label="Print chat">
        <wm-common-icon [data]="{iconName: printIcon}"></wm-common-icon>
        <wm-common-text class="chat-header-print-text" [data]="{ text: 'Print Transcript' }"></wm-common-text>
      </button>

      <button *ngIf="showHeaderClose" type="button" class="close-btn chat-header-close" (click)="closeChat()"
        aria-label="Close chat">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>

  <!-- Messages -->
  <div class="chat-body">
    <div class="chat-body-scroll" #chatBody>
      <!-- Loader -->
      <div class="center-wrapper" *ngIf="isLoadingMessages">
        <mat-spinner diameter="32"></mat-spinner>
      </div>

      <div class="center-wrapper" *ngIf="!isLoadingMessages && errorMessage">
        <div class="error-inline">
          <span class="material-icons error-icon">cancel</span>
          <span class="error-title">Error:</span>
          <span class="error-message">{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Updated by Amisha on 2026-09-03. Reason: use the approved empty-chat wording. -->
      <div class="center-wrapper" *ngIf="!isLoadingMessages && noMessages">
        <div class="no-messages">No chats available.</div>
      </div>

      <!-- Show messages only when not loading and messages exist -->
      <div *ngIf="!isLoadingMessages && messages.length" class="chat-messages-list">
        <div *ngFor="let msg of messages" class="message-block">
          <!-- Date Separator -->
          <div *ngIf="msg.showDate" class="chat-date">
            {{ msg.date | date: 'MMM d, y' }}
          </div>

          <!-- Actual Message -->
          <div class="chat-message" [ngClass]="msg.sender">
          <div class="profile-icon" [class.has-image]="!!msg.profileImageUrl">
            <img *ngIf="msg.profileImageUrl; else avatarFallback" class="chat-profile-image" [src]="msg.profileImageUrl"
              [alt]="msg.senderName + ' profile image'" (error)="onAvatarError(msg)" (load)="onAvatarLoad(msg, $event)" />

              <ng-template #avatarFallback>
                <span class="material-icons">person</span>
              </ng-template>
            </div>

            <div class="bubble-wrapper">
              <div class="bubble">{{ msg.text }}</div>

              <!-- Timestamp (for both user and agent) -->
              <div class="time">
                {{ msg.date | date: 'h:mm a' }}
              </div>

              <!-- User-only status -->
              <small *ngIf="msg.sender === 'user'" class="status">
                <ng-container [ngSwitch]="msg.status">
                  <ng-container *ngSwitchCase="'sent'">
                    sent
                    <span class="material-icons-outlined status-icon">send</span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'received'">
                    received
                    <span class="material-icons-outlined status-icon">check_circle_outline</span>
                  </ng-container>
                </ng-container>
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--
    <div *ngIf="isStaff && messages.length" class="chat-mail-actions">
      <button type="button" class="chat-mail-action-btn">
        SEND MAIL VIA SERVER
      </button>
      <button type="button" class="chat-mail-action-btn">
        SEND MAIL VIA OUTLOOK
      </button>
    </div>
    -->
  </div>

  <!-- Footer -->

  <div class="chat-footer" [class.chat-footer-no-mark-read]="!isStaff">
    <div *ngIf="showMarkAsReadControl" class="chat-footer-meta">
      <div class="mark-read-row">
        <mat-checkbox [(ngModel)]="markAsReadChecked"
          [disabled]="isUpdating || isMarkedReadByUser"
          (change)="onMarkReadChange($event.checked)">
        </mat-checkbox>

        <wm-common-text class="mark-read-text" [data]="{ text: 'Mark as Read' }">
        </wm-common-text>
      </div>
    </div>

    <form class="chat-form" (ngSubmit)="sendMessage()">
      <mat-form-field appearance="outline" class="chat-input">
        <mat-label>Type Message Here</mat-label>
        <textarea matInput [(ngModel)]="newMessage" name="message" cdkTextareaAutosize cdkAutosizeMinRows="1"
          cdkAutosizeMaxRows="3"></textarea>
      </mat-form-field>

      <!-- Desktop button -->
      <button mat-flat-button color="primary" [disabled]="!newMessage.trim()" type="submit">
        SEND
      </button>

      <!-- Mobile icon button -->
      <button type="submit" class="send-btn-inline">
        <span class="material-icons">send</span>
      </button>
    </form>
  </div>
</div>

<wm-common-confirmation-modal *ngIf="showErrorModal" [data]="errorModalConfig" (onOkClick)="showErrorModal = false"
  (onCloseClick)="showErrorModal = false">
</wm-common-confirmation-modal>

<div #printSection class="chat-print-template" aria-hidden="true">
  <div class="chat-print-sheet">
    <div class="chat-print-generated-on">
      {{ printTimestamp | date: 'M/d, h:mm a' }}
    </div>

    <div class="chat-print-meta">
      <div class="chat-print-meta-column">
        <div class="chat-print-meta-item">
          <span class="chat-print-meta-label">Application No:</span>
          <span>{{ displayApplicationNumber || '--' }}</span>
        </div>
        <div class="chat-print-meta-item">
          <span class="chat-print-meta-label">Application Type:</span>
          <span>{{ applicationType || '--' }}</span>
        </div>
      </div>

      <div class="chat-print-meta-column">
        <div class="chat-print-meta-item">
          <span class="chat-print-meta-label">Applicant Name:</span>
          <span>{{ displayApplicantName || '--' }}</span>
        </div>
        <div class="chat-print-meta-item">
          <span class="chat-print-meta-label">Site Address:</span>
          <span>{{ siteAddress || '--' }}</span>
        </div>
      </div>
    </div>

    <h2 class="chat-print-title">Chat</h2>

    <table *ngIf="messages.length; else noPrintableMessages" class="chat-print-table">
      <thead>
        <tr>
          <th class="chat-print-col-number">No.</th>
          <th>Name</th>
          <th class="chat-print-col-date">Date/Time</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let msg of messages; let index = index">
          <tr>
            <td>{{ index + 1 }}.</td>
            <td>{{ msg.senderName || '--' }}</td>
            <td>{{ msg.date ? (msg.date | date: 'd MMMM y h:mm:ss a') : '--' }}</td>
          </tr>
          <tr class="chat-print-comment-row">
            <td colspan="3">
              <span class="chat-print-comment-label">Comment :</span>
              <span>{{ msg.text || '--' }}</span>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>

    <ng-template #noPrintableMessages>
      <div class="chat-print-empty">
        <!-- Updated by Amisha on 2026-09-03. Reason: keep the approved empty-chat wording consistent. -->
        No chats available.
      </div>
    </ng-template>
  </div>
</div>
